import { motion } from "framer-motion";
import CustomLists from "@/components/elements/CustomLists/CustomLists";
import ListWithDescription from "@/components/elements/CustomLists/ListWithDescription";
import ImageWithContentLeft from "@/components/elements/ImageWithContent/ImageWithContentLeft";
import ImageWithContentRight from "@/components/elements/ImageWithContent/ImageWithContentRight";
import Paragraph from "@/components/elements/Paragraph/Paragraph";
import TitleBar from "@/components/elements/TitleBar/TitleBar";
import TitleWithContent from "@/components/elements/TitleWithContent/TitleWithContent";
import b1 from "@/assets/images/services/bituminousmembrane.jpeg"
import b2 from "@/assets/images/services/liquid membrane.jpg"
import b3 from "@/assets/images/services/bituminous_sheet.jpeg"

const fadeInUp = {
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

const fadeInLeft = {
  initial: { opacity: 0, x: -100 },
  whileInView: { opacity: 1, x: 0 },
  transition: { duration: 0.8, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

const fadeInRight = {
  initial: { opacity: 0, x: 100 },
  whileInView: { opacity: 1, x: 0 },
  transition: { duration: 0.8, ease: "easeOut" },
  viewport: { once: false, amount: 0.2 },
};

const BituminousWaterproofingMembrane = () => {
  return (
    <div className="pb-0 px-4  2xl:px-40 xl:px-40 md:px-[3rem] my-[5rem]">
      <TitleBar title="Bituminous Waterproofing Membrane" />

      <motion.div {...fadeInUp}>
        <ImageWithContentLeft alt="Bituminous Waterproofing Membrane"
          imageUrl={b1}
          title="Description"
          content="Bituminous coating is a popular waterproofing method that involves the application of a bitumen-based substance to surfaces for protection against water intrusion. Bitumen, a viscous and waterproof material derived from crude oil, serves as the primary component in these coatings. Bituminous coatings are widely used in various construction applications to provide effective waterproofing."
        />
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Key Feature of Bituminous Coating Waterproofing">
          <ListWithDescription
            items={[
              {
                title: "Waterproofing Properties:",
                content: ["Bituminous coatings create a durable and impermeable layer, preventing water from penetrating the underlying surfaces."]
              },
              {
                title: "Adhesion:",
                content: ["These coatings adhere well to a variety of substrates, including concrete, metal, and masonry, providing a secure bond."]
              },
              {
                title: "Flexibility:",
                content: ["Bituminous coatings often exhibit flexibility, allowing them to accommodate minor movements in the substrate without cracking."]
              },
              {
                title: "Versatility:",
                content: ["Bituminous coatings can be used on various surfaces, such as roofs, foundations, basements, and walls, making them versatile for different construction needs."]
              }
            ]}
          />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Why Bitumen is Popular in Waterproofing Sector">
          <Paragraph content="Bitumen has an economic process and versatility. It shows viscoelastic and thermoplastic behaviors and has a highly adhesive nature, making it a popular choice for waterproofing." />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Applications of Bituminous Waterproofing">
          <ListWithDescription
            items={[
              {
                title: "Roof Waterproofing:",
                content: ["Bituminous membranes are commonly used for waterproofing flat or sloping roofs, protecting them from rainwater and preventing leaks."]
              },
              {
                title: "Foundation Waterproofing:",
                content: ["Bituminous waterproofing helps create a protective barrier around building foundations, safeguarding against water seepage and damage."]
              },
              {
                title: "Basement Waterproofing:",
                content: ["Bituminous coatings or membranes are applied to basement walls and floors to prevent moisture infiltration and potential flooding."]
              },
              {
                title: "Tunnel Waterproofing:",
                content: ["Tunnels, whether for roads, railways, or underground utilities, can benefit from bituminous waterproofing to prevent water ingress and ensure structural integrity."]
              }
            ]}
          />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Advantages">
          <CustomLists
            items={[
              [
                'Excellent quality plugging compound-consistent performance.',
                'High early strength —plugging is completed within 5 minutes',
                'Simple Application',
                'Excellent adhesion'
              ],
              [
                'Water repellent concrete',
                'Prevents/reduce sweating, efflorescence saltpetre action',
                'Arrest shrinkage, better finish',
                'Very economical, required at very lesser dosage'
              ]
            ]}
          />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Different Forms of Using Bitumen in Waterproofing">
          <Paragraph content="Various forms of bitumen used for waterproofing include:" />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInLeft}>
        <ImageWithContentLeft alt="Bituminous Waterproofing Membrane"
          imageUrl={b2}
          title="Bituminous Paint"
          content="Bituminous paints consist of a mixture of bitumen with an organic solvent. Adding polymer to them leads to their stability and better performance in waterproofing."
        />
      </motion.div>

      <motion.div {...fadeInRight}>
        <ImageWithContentRight alt="Bituminous Waterproofing Membrane"
          imageUrl={b3}
          title="Bituminous Membrane"
          content="The bituminous membrane is used for sealing low-slope roofs to prevent water penetration and protect the building structure."
        />
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title="Steps for Bituminous Protective Coating Waterproofing">
          <CustomLists
            layout="stacked"
            listStyle="decimal"
            items={[[
              'Surface preparation is key for Bituminous Protective coating before application.',
              'The surface must be clean and free from contamination such as dust, oil, grease, and organic growth.',
              'Remove all loosely adhering particles such as rust, mortar, and cement laitance.',
              'The surface would be cleaned by wire or stiff brushing and finally soft brushing or compressed air.',
              'Cracks greater than 1.6mm wide should be routed out.',
              'Substrate condition shall be approved by the client prior to application.'
            ]]} />
        </TitleWithContent>
      </motion.div>

      <motion.div {...fadeInUp}>
        <TitleWithContent title='Why Choose Us for Bituminous Coating Waterproofing?'>
          <Paragraph
            content={
              <>
                At <span className="span-text">Sheel Waterproofing</span>, we provide high-quality bituminous coating waterproofing solutions that offer superior protection against water ingress for a variety of structures. Our coatings are perfect for basements, foundations, and retaining walls. We focus on delivering long-lasting, cost-effective results with the following benefits:
              </>
            }
          />
          <CustomLists
            listStyle="none"
            layout="stacked"
            items={[[ 
              'Excellent protection against water penetration for underground and exposed surfaces',
              'Strong adhesion to concrete, masonry, and other surfaces',
              'Resistant to extreme weather conditions, UV exposure, and chemical attacks',
              'Flexible and durable, ensuring long-term performance and reliability',
            ]]}
            bullets={[["🏢", "🌧️", "🛠️", "💪"]]}
          />
          <Paragraph
            content={
              <>
                Bituminous coatings are ideal for areas where water pressure and exposure to harsh environments are significant concerns. With <span className="span-text">Sheel Waterproofing</span>, you get effective, long-lasting waterproofing that ensures peace of mind for your property.
                <br /><br />
                <a href="/contact" className="text-custom-hov underline">Contact us today</a> for a free consultation or site inspection.
              </>
            }
          />
        </TitleWithContent>
      </motion.div>
    </div>
  );
};

export default BituminousWaterproofingMembrane;